/**
 * Composer - Custom composer for the assistant that integrates
 * skills/tools UI with Assistant-UI's runtime.
 *
 * Always uses MentionInput for text entry. When skills are enabled,
 * registers a <Mention> child for slash command support.
 *
 * The assistant-ui composer is the single source of truth for text.
 * Draft persistence is handled by useDraftSync, which observes
 * composer.text and debounce-saves to localStorage as a side-effect.
 */
import { useEffect, useRef, useState } from "react";
import { ComposerPrimitive, useAui, useAuiState } from "@assistant-ui/react";
import { ArrowUpIcon, SquareIcon } from "lucide-react";
import { useAgentEvents } from "~/components/assistant-v2/AgentEventProvider";
import { ContextBufferMeter } from "~/components/assistant-v2/ContextBufferMeter";
import { TooltipIconButton } from "~/components/assistant-v2/TooltipIconButton";
import type { ContextBuffer } from "~/components/assistant-v2/useContextBuffer";
import { useDraftSync } from "~/components/assistant-v2/useDraftSync";
import { MentionInput, Mention, type MentionInputRef } from "~/components/assistant/MentionInput";
import {
  SkillEditorMode,
  type SkillFormData,
  type SkillFormErrors,
} from "~/components/assistant/SkillEditorForm";
import SkillEditorModal from "~/components/assistant/SkillEditorModal";
import { SkillSelector } from "~/components/assistant/SkillSelector";
import { SkillsPopup } from "~/components/assistant/SkillsPopup";
import { ToolSelector } from "~/components/assistant/ToolSelector";
import { ZENDESK_AI_SKILLS_URL } from "~/components/assistant/constants";
import type { MCPServer, McpGuidelines, SkillUpdate } from "~/components/assistant/types";
import { cn } from "~/lib/utils";
import type { PartialSkill, Skill, SkillFields } from "~/services/skills";

export interface ComposerProps {
  contextId: string;
  conversationId: string | null;
  skillsEnabled?: boolean;
  /** Skills available in this conversation — AGENT ∪ DATASET ∪ USER union. */
  skills?: PartialSkill[];
  isLoadingSkills?: boolean;
  /** Create a new USER-scope skill in the caller's personal library. */
  onCreateUserSkill?: (fields: SkillFields) => Promise<void>;
  onUpdateSkill?: (skillId: string, skill: SkillUpdate) => Promise<void>;
  /** Delete/detach a skill — handler dispatches by skill.scope. */
  onDeleteSkill?: (skill: PartialSkill) => Promise<void>;
  onGetSkill?: (skillId: string) => Promise<Skill>;
  mcpServers?: MCPServer[];
  selectedMcpServers?: string[];
  onToggleMcpServer?: (serverId: string) => void;
  showToolSelector?: boolean;
  placeholder?: string;
  contextBuffer?: ContextBuffer | null;
  mcpGuidelines?: McpGuidelines;
  /** Maps dataset scope_id → dataset display name for "from dataset <name>" source chips. */
  datasetNames?: Record<string, string>;
}

/**
 * Registers a ModelContextProvider that injects selected MCP server IDs
 * into the AG-UI request's forwardedProps. The AG-UI runtime spreads
 * ModelContext.config into forwardedProps in buildRunInput().
 *
 * Must be called inside AssistantRuntimeProvider.
 */
function useForwardMcpServers(selectedMcpServers: string[]) {
  const aui = useAui();

  useEffect(() => {
    return aui.modelContext().register({
      getModelContext: () => {
        return {
          config: { servers: selectedMcpServers } as any, // eslint-disable-line @typescript-eslint/no-explicit-any
        };
      },
    });
  }, [aui, selectedMcpServers]);
}

export function Composer({
  contextId,
  conversationId,
  skillsEnabled = false,
  skills = [],
  isLoadingSkills = false, // eslint-disable-line @typescript-eslint/no-unused-vars
  onCreateUserSkill,
  onUpdateSkill,
  onDeleteSkill,
  onGetSkill,
  mcpServers = [],
  selectedMcpServers = [],
  onToggleMcpServer,
  showToolSelector = true,
  placeholder = "Send a message...",
  contextBuffer,
  mcpGuidelines,
  datasetNames,
}: ComposerProps) {
  const { isRunningRemotely, isCancelling, onCancelRun } = useAgentEvents();
  useForwardMcpServers(selectedMcpServers);

  const aui = useAui();
  const composer = aui.composer();
  const isRunning = useAuiState((s) => s.thread.isRunning);
  const composerText = useAuiState((s) => s.composer.text);
  // Disable the composer when a human-in-the-loop tool (e.g. approval card)
  // is awaiting user interaction. Sending a message in this state causes an
  // LLM error because the tool call hasn't been resolved yet.
  // Note: message.status never reaches "requires-action" because the
  // external-store runtime auto-computes status with hasPendingToolCalls=false.
  // Check the actual content instead: unresolved tool-call parts = pending.
  const needsAction = useAuiState((s) => {
    if (s.thread.isRunning) return false;
    const msgs = s.thread.messages;
    const lastMsg = msgs[msgs.length - 1];
    if (lastMsg?.role !== "assistant") return false;
    return lastMsg.content.some((p) => p.type === "tool-call" && p.result === undefined);
  });

  const mentionInputRef = useRef<MentionInputRef>(null);
  const { clearDraft } = useDraftSync({ contextId, conversationId });

  // Skill editor state
  const [skillEditorOpen, setSkillEditorOpen] = useState(false);
  const [skillEditorErrors, setSkillEditorErrors] = useState<SkillFormErrors>({});
  const [savingSkill, setSavingSkill] = useState(false);
  const [editingSkillId, setEditingSkillId] = useState<string | null>(null);
  const [editingSkillData, setEditingSkillData] = useState<Partial<SkillFormData> | undefined>(
    undefined
  );
  const [loadingSkillContent, setLoadingSkillContent] = useState(false);

  const handleInputChange = (value: string) => {
    composer.setText(value);
  };

  const handleSend = () => {
    if (!composerText.trim() || isRunning || isRunningRemotely || needsAction) {
      return;
    }
    composer.send();
    clearDraft();
  };

  const handleCancel = () => {
    onCancelRun?.();
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Skill CRUD handlers
  const handleAddSkill = () => {
    setEditingSkillId(null);
    setEditingSkillData(undefined);
    setSkillEditorOpen(true);
    setSkillEditorErrors({});
  };

  const handleEditSkill = async (skill: PartialSkill) => {
    if (!onGetSkill) {
      return;
    }

    setEditingSkillId(skill.id);
    setEditingSkillData(undefined);
    setSkillEditorOpen(true);
    setSkillEditorErrors({});
    setLoadingSkillContent(true);

    try {
      const full = await onGetSkill(skill.id);
      setEditingSkillData({
        name: full.name,
        description: full.description ?? "",
        content: full.content,
      });
    } catch (error) {
      console.error("Failed to load skill:", error);
      setSkillEditorErrors({ general: "Failed to load skill content" });
    } finally {
      setLoadingSkillContent(false);
    }
  };

  const handleDeleteSkill = async (skill: PartialSkill) => {
    if (!onDeleteSkill) {
      return;
    }
    try {
      await onDeleteSkill(skill);
    } catch (error) {
      console.error("Failed to delete skill:", error);
    }
  };

  const handleSaveSkill = async (data: SkillFormData) => {
    if (!onCreateUserSkill || !onUpdateSkill) {
      return;
    }

    setSavingSkill(true);
    setSkillEditorErrors({});

    try {
      const fields: SkillFields = {
        name: data.name,
        description: data.description ?? "",
        content: data.content,
      };

      if (editingSkillId) {
        await onUpdateSkill(editingSkillId, fields);
      } else {
        await onCreateUserSkill(fields);
      }

      setSkillEditorOpen(false);
      setEditingSkillId(null);
      setEditingSkillData(undefined);
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to save skill";
      setSkillEditorErrors({ general: message });
    } finally {
      setSavingSkill(false);
    }
  };

  const hasFeatures = showToolSelector || skillsEnabled;

  return (
    <>
      <ComposerPrimitive.Root className="aui-composer-root relative flex w-full flex-col">
        <div className="flex w-full flex-col rounded-2xl border border-black/[0.12] bg-background px-1 pt-2 outline-none transition-all duration-200 hover:ring-1 hover:ring-gray-300 focus-within:ring-1 focus-within:ring-gray-300">
          <div className="mb-1 px-3 pt-2 pb-3">
            <MentionInput
              ref={mentionInputRef}
              value={composerText}
              onChange={handleInputChange}
              onKeyDown={handleKeyPress}
              placeholder={needsAction ? "Respond to the tool call above..." : placeholder}
              maxHeight={128}
              autoFocus
              disabled={needsAction}
            >
              {skillsEnabled && (
                <Mention
                  trigger="/"
                  type="skill"
                  pattern={/\/[\w-]+/g}
                  className="text-block rounded px-1.5 py-0 inline-block mx-0.5 my-0.25"
                  data={skills}
                  displayTransform={(skill: PartialSkill) => skill.name}
                  renderSuggestions={(props) => (
                    <div
                      style={{
                        position: "fixed",
                        top: props.position.top - 8,
                        left: props.position.left,
                        transform: "translateY(-100%)",
                        zIndex: 50,
                      }}
                      className="bg-white rounded-lg shadow-[0_0_0_1px_rgba(84,72,49,0.08),0_3px_8px_rgba(0,0,0,0.12)] overflow-hidden w-84"
                    >
                      <SkillsPopup
                        skills={skills}
                        searchFilter={props.searchText}
                        onSelectSkill={(name) => props.onSelect(`/${name}`)}
                        onAddSkill={() => {
                          handleAddSkill();
                          props.onClose();
                        }}
                        onEditSkill={(id) => {
                          handleEditSkill(id);
                          props.onClose();
                        }}
                        onDeleteSkill={handleDeleteSkill}
                        onLearnMore={() =>
                          window.open(ZENDESK_AI_SKILLS_URL, "_blank", "noopener,noreferrer")
                        }
                        enableKeyboardNav={true}
                        onClose={props.onClose}
                        datasetNames={datasetNames}
                      />
                    </div>
                  )}
                />
              )}
            </MentionInput>
          </div>

          <div className="relative mx-2 mb-2 flex items-center justify-between">
            {/* Left: Tools and skills selectors */}
            <div className="flex items-center gap-0.5">
              {showToolSelector && onToggleMcpServer && (
                <ToolSelector
                  servers={mcpServers}
                  selectedServerIds={selectedMcpServers}
                  onToggle={onToggleMcpServer}
                  mcpGuidelines={mcpGuidelines}
                />
              )}
              {skillsEnabled && (
                <>
                  <SkillSelector
                    skills={skills}
                    onSelectSkill={(name) => {
                      composer.setText(composerText + `/${name} `);
                      setTimeout(() => {
                        mentionInputRef.current?.focusAtEnd();
                      }, 0);
                    }}
                    onAddSkill={handleAddSkill}
                    onEditSkill={handleEditSkill}
                    onDeleteSkill={handleDeleteSkill}
                    datasetNames={datasetNames}
                  />
                </>
              )}
              {!hasFeatures && <div />}
            </div>

            {/* Right: Context meter + Send/Cancel */}
            <div className="flex items-center gap-3">
              {contextBuffer && <ContextBufferMeter buffer={contextBuffer} />}
              <ComposerAction
                isRunning={isRunning || isRunningRemotely}
                isCancelling={isCancelling}
                onSend={handleSend}
                onCancel={handleCancel}
                canSend={composerText.trim().length > 0 && !needsAction}
              />
            </div>
          </div>
        </div>
      </ComposerPrimitive.Root>

      {/* Skill modals */}
      {skillsEnabled && (
        <>
          {skillEditorOpen && (
            <SkillEditorModal
              key={editingSkillId ?? "new"}
              open={skillEditorOpen}
              initialData={editingSkillData}
              onClose={() => {
                setSkillEditorOpen(false);
                setEditingSkillId(null);
                setEditingSkillData(undefined);
                setSkillEditorErrors({});
              }}
              onSubmit={handleSaveSkill}
              loading={savingSkill || loadingSkillContent}
              errors={skillEditorErrors}
              mode={editingSkillId ? SkillEditorMode.EDIT : SkillEditorMode.CREATE}
            />
          )}
        </>
      )}
    </>
  );
}

function ComposerAction({
  isRunning,
  isCancelling,
  onSend,
  onCancel,
  canSend,
}: {
  isRunning: boolean;
  isCancelling: boolean;
  onSend: () => void;
  onCancel: () => void;
  canSend: boolean;
}) {
  const showStop = isRunning || isCancelling;

  if (showStop) {
    return (
      <TooltipIconButton
        tooltip="Stop agent"
        side="bottom"
        type="button"
        variant="default"
        size="icon"
        className={cn("size-7 rounded-full", isCancelling && "bg-gray-400")}
        aria-label="Stop agent"
        onClick={onCancel}
        disabled={isCancelling}
      >
        <SquareIcon className="size-3 fill-current" />
      </TooltipIconButton>
    );
  }

  return (
    <TooltipIconButton
      tooltip="Send message"
      side="bottom"
      type="button"
      variant="default"
      size="icon"
      className="size-7 rounded-full bg-primary hover:bg-primary/80 disabled:bg-gray-400"
      aria-label="Send message"
      onClick={onSend}
      disabled={!canSend}
    >
      <ArrowUpIcon className="size-4" />
    </TooltipIconButton>
  );
}
